import { PropsWithChildren, createContext, useEffect, useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { storageKeys } from '@/app/constants/storageKeys';
import { EventNames, Events } from '@/app/libs/analytics/constants/events';
import { sendClickEvent } from '@/app/libs/analytics/utils/send-click-event';
import { BreadcrumbCategory } from '@/app/libs/sentry/types/breadcrumb';
import { addBreadcrumb } from '@/app/libs/sentry/utils/breadcrumb';
import { IProductions, IUser } from '@/app/models/user';
import { authService, getIpAddress } from '@/app/services/authService';
import { storage } from '@/app/storage';
import { LaunchScreen } from '@/view/components/modules/launch-screen';

interface AuthContextValue {
  signout: VoidFunction;
  signedIn: boolean;
  selectedProduct: string;
  user: IUser | undefined;
  productName: string | undefined;
  allowedProductions: IProductions[];
  selectProduct(product: string): void;
  signin(accessToken: string, refreshToken: string): void;
}

const AuthContext = createContext({} as AuthContextValue);

const AuthProvider = ({ children }: PropsWithChildren) => {
  const [signedIn, setSignedIn] = useState<boolean>(() => {
    const storedAccessToken = getAccessToken();
    return Boolean(storedAccessToken);
  });

  const [selectedProduct, setSelectedProduct] = useState<string>(() => {
    const storedSelectedProduct = storage.get(storageKeys.SELECTED_PRODUCT);

    return storedSelectedProduct;
  });

  const queryClient = useQueryClient();

  const { data, isSuccess, isFetching } = useQuery({
    gcTime: Infinity,
    enabled: signedIn,
    staleTime: Infinity,
    queryKey: ['users', 'me'],
    queryFn: () => authService.me(getAccessToken()),
  });

  function getAccessToken() {
    const storedAccessToken = storage.get(storageKeys.ACCESS_TOKEN);
    return storedAccessToken;
  }

  function signin(accessToken: string, refreshToken: string) {
    storage.add(storageKeys.ACCESS_TOKEN, accessToken);
    storage.add(storageKeys.REFRESH_TOKEN, refreshToken);

    setSignedIn(true);

    sendClickEvent({ label: Events.LOGIN_USER, name: EventNames.LOGIN_USER });
  }

  function signout() {
    storage.remove(storageKeys.ACCESS_TOKEN);
    storage.remove(storageKeys.REFRESH_TOKEN);
    storage.remove(storageKeys.SELECTED_PRODUCT);

    queryClient.clear();
    setSignedIn(false);
    setSelectedProduct('');

    sendClickEvent({ label: Events.LOGOUT_USER, name: EventNames.LOGOUT_USER });
  }

  function selectProduct(product: string) {
    if (!Number(product) && !allowedProductions.find((production) => production.code === product)) {
      return;
    }

    storage.add(storageKeys.SELECTED_PRODUCT, product);
    setSelectedProduct(product);

    sendClickEvent({
      value: product,
      label: Events.PRODUCT_CHANGE,
      name: EventNames.PRODUCT_CHANGE,
    });

    addBreadcrumb(`Product changed to ${product}`, 'info', BreadcrumbCategory.UI_CLICK);
  }

  const allowedProductions = data?.productions ?? [];

  const productName = allowedProductions.find(
    (product) => Number(product.code) === Number(selectedProduct),
  )?.name;

  useEffect(() => {
    if (!isSuccess || !data) return;

    const storedSelectedProduct = storage.get(storageKeys.SELECTED_PRODUCT);
    const productions = data.productions ?? [];

    if (productions.length === 0) {
      storage.remove(storageKeys.SELECTED_PRODUCT);
      setSelectedProduct('');
      return;
    }

    if (productions.length === 1 && !storedSelectedProduct) {
      const defaultProduct = productions[0].code.toString();
      storage.add(storageKeys.SELECTED_PRODUCT, defaultProduct);
      setSelectedProduct(defaultProduct);
      return;
    }

    const isAllowed = productions.some(
      (product) => Number(product.code) === Number(storedSelectedProduct),
    );

    if (storedSelectedProduct && isAllowed) {
      setSelectedProduct(storedSelectedProduct);
      return;
    }
  }, [isSuccess, data]);

  useEffect(() => {
    if (!signedIn) return;

    async function fetchAndStoreIp() {
      try {
        const response = await getIpAddress();
        const ip = await response.text();

        storage.add(storageKeys.IP_ADDRESS, ip);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Erro ao buscar IP do usuário:', error);
      }
    }

    fetchAndStoreIp();
  }, [signedIn]);

  return (
    <AuthContext.Provider
      value={{
        signin,
        signout,
        user: data,
        productName,
        selectProduct,
        selectedProduct,
        allowedProductions,
        signedIn: isSuccess && signedIn,
      }}
    >
      <LaunchScreen isLoading={isFetching} />
      {!isFetching && children}
    </AuthContext.Provider>
  );
};

export { AuthContext, AuthProvider, type AuthContextValue };
